import { useState, useEffect, useCallback } from "react";
import { Wallet, TrendingDown, PlusCircle } from "lucide-react";
import { Shimmer, AnalyticsCardsShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import dayjs from "dayjs";
import DateRangeFilter, {
  getDefaultRange,
  toISO,
  type DateRange,
} from "@/components/common/DateRangeFilter";
import DailyPurchasesChart from "@/components/wallet/DailyPurchasesChart";
import TransactionHistory from "@/components/wallet/TransactionHistory";
import { useAuth } from "@/context/AuthContext";
import {
  getWalletBalance,
  getWalletAnalytics,
  getDailyPurchases,
  type WalletAnalytics,
} from "@/utils/wallet-api";

function formatCredits(paise: number): string {
  const credits = paise / 100;
  return Math.abs(credits).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }) + " Credits";
}

function ComparisonLabel({ current, previous, label }: { current: number; previous: number; label: string }) {
  if (previous === 0) return <span className="text-xs text-muted-foreground">{label}</span>;
  const diff = current - previous;
  const pct = ((diff / Math.abs(previous)) * 100).toFixed(1);
  const isUp = diff >= 0;
  return (
    <span className={`text-xs ${isUp ? "text-green-600" : "text-red-600"}`}>
      {isUp ? "↑" : "↓"} {Math.abs(Number(pct))}% vs yesterday
    </span>
  );
}

export default function WalletPage() {
  const { userId, token } = useAuth();

  const [balance, setBalance] = useState<number | null>(null);
  const [analytics, setAnalytics] = useState<WalletAnalytics | null>(null);
  const [dailyTxns, setDailyTxns] = useState<Array<{ amount: number; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState("");
  const [chartRange, setChartRange] = useState<DateRange>(getDefaultRange);

  // Fetch balance + analytics
  const fetchAnalytics = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");

    const todayEnd = dayjs().endOf("day").toISOString();
    const todayStart = dayjs().startOf("day").toISOString();
    const yesterdayStart = dayjs().subtract(1, "day").startOf("day").toISOString();

    try {
      const [bal, ana] = await Promise.all([
        getWalletBalance(userId, token),
        getWalletAnalytics(userId, todayStart, todayEnd, yesterdayStart, token),
      ]);
      setBalance(bal);
      setAnalytics(ana);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load wallet data");
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Fetch daily purchases for chart
  const fetchDailyPurchases = useCallback(async () => {
    if (!userId || !token) return;
    setChartLoading(true);

    try {
      const data = await getDailyPurchases(userId, toISO(chartRange.from), toISO(chartRange.to), token);
      setDailyTxns(data);
    } catch {
      // Non-critical — chart just stays empty
    } finally {
      setChartLoading(false);
    }
  }, [userId, token, chartRange]);

  useEffect(() => {
    fetchDailyPurchases();
  }, [fetchDailyPurchases]);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight mb-1">
          Wallet
        </h1>
        <p className="text-base text-muted-foreground">
          Manage your wallet balance, view spending analytics, and transaction history.
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6 rounded-lg">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Analytics Cards */}
      <Shimmer loading={loading} fallback={<AnalyticsCardsShimmer />}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          {/* Available Balance */}
          <Card className="h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wallet className="size-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">
                  Available Balance
                </span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight">
                {balance !== null ? formatCredits(balance) : "—"}
              </h2>
              <span className="text-xs text-muted-foreground">
                Total credits balance
              </span>
            </CardContent>
          </Card>

          {/* Today's Spending */}
          <Card className="h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="size-5 text-red-600" />
                <span className="text-sm font-medium text-muted-foreground">
                  Today's Spending
                </span>
              </div>
              {analytics ? (
                <>
                  <h2 className="text-3xl font-bold tracking-tight text-red-600">
                    {formatCredits(Math.abs(analytics.todaySpent))}
                  </h2>
                  <ComparisonLabel
                    current={Math.abs(analytics.todaySpent)}
                    previous={Math.abs(analytics.yesterdaySpent)}
                    label="No spending yesterday"
                  />
                </>
              ) : (
                <h2 className="text-3xl font-bold tracking-tight text-red-600">—</h2>
              )}
            </CardContent>
          </Card>

          {/* Today's Additions */}
          <Card className="h-full">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <PlusCircle className="size-5 text-green-600" />
                <span className="text-sm font-medium text-muted-foreground">
                  Today's Additions
                </span>
              </div>
              {analytics ? (
                <>
                  <h2 className="text-3xl font-bold tracking-tight text-green-600">
                    {formatCredits(analytics.todayAdded)}
                  </h2>
                  <ComparisonLabel
                    current={analytics.todayAdded}
                    previous={analytics.yesterdayAdded}
                    label="No additions yesterday"
                  />
                </>
              ) : (
                <h2 className="text-3xl font-bold tracking-tight text-green-600">—</h2>
              )}
            </CardContent>
          </Card>
        </div>
      </Shimmer>

      {/* Daily Purchases Chart */}
      <div className="mb-6">
        <div className="flex justify-end mb-4">
          <DateRangeFilter value={chartRange} onChange={setChartRange} />
        </div>
        <DailyPurchasesChart transactions={dailyTxns} loading={chartLoading} />
      </div>

      {/* Transaction History */}
      <TransactionHistory />
    </div>
  );
}
